import { Button, Dialog, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import biApi from "../../../../../api/biApi";
import type { WorkbookTemplate } from "../../../../../api/types/workbookRuns.types";
import useRunDialogForm from "./hooks/useRunDialogForm";
import RunNameSelector from "./RunNameSelector";
import WorkbookFiltersSelector from "./WorkbookFiltersSelector";
import WorkbookTemplateSelector from "./WorkbookTemplateSelector";

interface Props {
  onClose: () => void;
  onStartRun: (runId: string) => void;
  template?: WorkbookTemplate;
}

const runWorkbook = withErrorHandling(biApi.runWorkbook);

const NewRunDialog = ({ onClose, onStartRun, template }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [isRunning, setIsRunning] = useState(false);

  const {
    name,
    selectedTemplate,
    filters,
    isValid,
    nameError,
    changeName,
    changeSelectedTemplate,
    updateDateFilter,
    updateTextFilter,
  } = useRunDialogForm(template);

  const getWorkbookTemplates = useCallback((signal?: AbortSignal) => biApi.getAllWorkbookTemplates(signal), []);

  const [templates, error, { isFetching }] = useFetch(getWorkbookTemplates);
  const isFetchingTemplates = isFetching || templates === undefined;

  const handleSelectTemplate = (templateId: string) => {
    const selectedTemplate = templates?.find((t) => t.id === templateId);
    if (!selectedTemplate) {
      return;
    }
    changeSelectedTemplate(selectedTemplate);
  };

  const handleStartRun = async () => {
    if (!selectedTemplate) {
      return;
    }
    setIsRunning(true);
    const [runResponse, error] = await runWorkbook({
      templateId: selectedTemplate.id,
      workbookFilters: filters,
      name: name,
    });
    if (error) {
      sendNotificationError(error.message);
      setIsRunning(false);
      return;
    }
    sendNotification("Run has been started");
    onStartRun(runResponse.runId);
    setIsRunning(false);
  };

  const hasFilters = filters.length > 0;
  const isStartRunDisabled = !isValid || (!isValid && (isFetchingTemplates || templates === undefined)) || !!error;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      sx={{ ".MuiDialog-container": { alignItems: "start", pt: 20 }, height: "100%" }}
      slotProps={{ paper: { sx: { minHeight: 250 } } }}
    >
      <DialogTitle>Create Workbook Run</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      {error ? (
        <DialogContent>
          <DataLoadingFailed title="Failed to load workbook run templates" />
        </DialogContent>
      ) : (
        <>
          <DialogContent sx={{ minHeight: "max-content" }}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <WorkbookTemplateSelector
                isFetchingTemplates={isFetchingTemplates ?? false}
                templates={templates}
                templateId={selectedTemplate?.id ?? ""}
                setTemplateId={handleSelectTemplate}
              />
              <RunNameSelector name={name} setName={changeName} error={!!nameError} errorMessage={nameError} />
              {hasFilters && (
                <Typography variant="h6" sx={{ mt: 2.5, pt: 2 }}>
                  Workbook Filters
                </Typography>
              )}
            </Stack>
          </DialogContent>
          {hasFilters && (
            <DialogContent sx={{ pt: 0 }}>
              <WorkbookFiltersSelector
                filters={filters}
                onUpdateDateFilter={updateDateFilter}
                onUpdateTextFilter={updateTextFilter}
              />
            </DialogContent>
          )}
        </>
      )}
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" onClick={handleStartRun} disabled={isStartRunDisabled} loading={isRunning}>
          Run Workbook
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default NewRunDialog;
